/**
 * Minimal fetch wrapper for backend API calls.
 * All requests go through Vite's dev-server proxy in development
 * (/api/* -> http://localhost:3001) and through the same origin in production
 * (assuming frontend and backend are hosted together or behind a reverse proxy).
 */

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const body: unknown = isJson ? await res.json() : await res.text()

  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`
    throw new ApiError(res.status, body, message)
  }

  return body as T
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { method: 'GET' })
  return handleResponse<T>(res)
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleResponse<T>(res)
}
